<script setup lang="ts">
    import ModalBase from '@/shared/components/ModalBase.vue';
    import MarkdownEditorComponent from './MarkdownEditorComponent.vue';
    import { computed, nextTick, ref, watch } from 'vue';
    import { InputRegexValidator } from '@/shared/helpers/inputRegexValidator.ts';

    const emits = defineEmits<{
        reset: [void];
        done: [string | null];
    }>();

    const props = defineProps({
        value: {
            type: String,
            required: true
        },
        title: {
            type: String,
            default: 'Edit'
        },
        showPreview: {
            type: Boolean,
            default: true
        },
        regexValidator: {
            type: InputRegexValidator,
            default: null
        }
    });

    const valueCopy = ref<string>('');
    const editorRef = ref<InstanceType<typeof MarkdownEditorComponent> | null>(null);

    watch(
        () => props.value,
        (newValue) => {
            valueCopy.value = newValue ?? '';
        },
        { immediate: true }
    );

    async function onReset() {
        emits('reset');

        // The modal input is local state. The parent "reset" handler may or may not change the prop value,
        // so we sync explicitly to ensure the textarea resets immediately.
        await nextTick();
        valueCopy.value = props.value ?? '';
    }

    function onDone() {
        valueCopy.value = valueCopy.value.trim();
        emits('done', valueCopy.value === '' ? null : valueCopy.value);
    }

    const validInput = computed(() => {
        return editorRef.value?.validInput ?? true;
    });
</script>

<template>
    <modal-base
        :can-cancel="false"
        max-width="90vw"
        width="100rem">
        <template #modal-header>
            {{ props.title }}
        </template>
        <template #modal-body>
            <div class="markdown-editor-modal-body">
                <markdown-editor-component
                    ref="editorRef"
                    v-model="valueCopy"
                    :show-preview="props.showPreview"
                    :regex-validator="props.regexValidator" />
            </div>
        </template>
        <template #modal-footer>
            <b-field class="is-grouped-right">
                <div class="buttons">
                    <b-button @click="onReset">
                        Reset changes
                    </b-button>
                    <b-button
                        :disabled="!validInput"
                        type="is-primary"
                        @click="onDone">
                        Done
                    </b-button>
                </div>
            </b-field>
        </template>
    </modal-base>
</template>

<style scoped>
    /* Provide a sensible default height so the embedded editor has room to fill. */
    .markdown-editor-modal-body {
        height: 60vh;
    }
</style>
